import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  ErrorStateInfo,
  Grid,
  IconButton, Progress,
  Typography,
} from 'web-library';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useDispatch } from 'react-redux';
import { disableIdleTimeout, enableIdleTimeout } from '../../store/appSlice';
import { FileUploadState } from '../../models/File';

const useStyles = makeStyles()((theme) =>
({
  dialogContainer: {
    display: 'flex',
    margin: theme.spacing(2),
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    justifyItems: 'center',
  },
  dialogContent: {
    padding: theme.spacing(1),
  },
  dialogProcess: {
    color: theme.color.text.textHeader,
  },
  titleDialog: {
    paddingBottom: theme.spacing(6),
  },
  titleDialogErr: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),

    '& img': {
      width: theme.spacing(16),
      height: theme.spacing(16),
      margin: 0,
      marginTop: theme.spacing(0.5),
    },
  },
  subTitle: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(7),
  },
  closeButton: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  errorTitle: {
    marginBottom: theme.spacing(1),
    color: theme.color.icon.iconTrash,
  },
  subError: {
    marginBottom: theme.spacing(5),
  },
}));

export interface ExportResultProgressModalProps {
  /** to define downloading status
   * * */
  fileDownloadStatus: FileUploadState;

  /** to define modal open/close state
   * */
  modalState: boolean;

  /** to handle dialog close
   * */
  onDialogClose?: () => void;
}

const ExportResultProgressModal: React.FC<ExportResultProgressModalProps> = (
  props: ExportResultProgressModalProps
) => {
  const {
    modalState = false,
    fileDownloadStatus = 'UPLOADING',
    onDialogClose = () => null,
  } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Disable the idle timer countdown when downloading document.
  useEffect(() => {
    if (modalState) {
      dispatch(disableIdleTimeout());
    } else {
      dispatch(enableIdleTimeout());
    }
  }, [dispatch, modalState]);

  return (
    (<Dialog
      id="opadmin-download-dialog"
      className={classes.dialogContainer}
      fullWidth
      open={modalState}
      maxWidth="sm"
    >
      {(fileDownloadStatus === 'TIMEOUT' || fileDownloadStatus === 'FAILED') && (
        <Box className={classes.titleContainer}>
          <IconButton
            className={classes.closeButton}
            id="button-view-detail-close"
            name={IconName.cross}
            iconSize="M"
            color={theme.color.icon.iconDefault}
            ariaLabel="view-subject-detail-close"
            onClick={onDialogClose} />
        </Box>
      )}
      <DialogContent className={classes.dialogContent}>
        {(fileDownloadStatus === 'UPLOADING' ||
          fileDownloadStatus === 'IDLE' ||
          fileDownloadStatus === 'GENERATING') && (
            <Grid className={classes.titleDialog} wrap="wrap" container direction="column">
              <Grid xs={12}>
                <Typography className={classes.dialogProcess} display="inline" variant="h4">
                  {t('admin->>ResultRelease.InProgress')}
                </Typography>
              </Grid>
            </Grid>
          )}

        {(fileDownloadStatus === 'TIMEOUT' || fileDownloadStatus === 'FAILED') && (
          <Grid className={classes.titleDialogErr}>
            <ErrorStateInfo>{[]}</ErrorStateInfo>
          </Grid>
        )}
        {(fileDownloadStatus === 'UPLOADING' ||
          fileDownloadStatus === 'IDLE' ||
          fileDownloadStatus === 'GENERATING') && (
            <Grid wrap="wrap" container direction="column" alignItems="center">
              <Grid xs={12}>
                <Progress size="XL" type="circle" />
              </Grid>
              <Typography className={classes.subTitle} display="inline" variant="h6">
                Loading...
              </Typography>
            </Grid>
          )}
        {(fileDownloadStatus === 'TIMEOUT' || fileDownloadStatus === 'FAILED') && (
          <Grid wrap="wrap" container direction="column" alignItems="center">
            <Typography className={classes.errorTitle} display="inline" variant="h4">
              {t('admin->>ResultRelease.ExportError')}
            </Typography>
            <Typography className={classes.subError} display="inline">
              Please try again later.
            </Typography>
          </Grid>
        )}
      </DialogContent>
    </Dialog>)
  );
};
export default ExportResultProgressModal;
